import { useState, useEffect, useCallback } from "react"
import { useAuth } from "@/hooks/useAuth"
import { useRole } from "@/hooks/useRole"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog"
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "@/components/ui/alert-dialog"
import { Skeleton } from "@/components/ui/skeleton"
import {
  listUsers,
  inviteUser,
  updateUserRole,
  updateUserStatus,
  setUserDynamicAgents,
  deleteUser,
  resendInvite,
  type User,
} from "@/services/adminService"
import { UserPlus, Trash2, MailPlus } from "lucide-react"

const ROLES = ["owner", "admin", "member", "viewer"] as const

export default function UserManagement() {
  const { token, user: authUser } = useAuth()
  // ロール編集・招待・有効/無効・削除は owner のみ（2026-06-14）。admin は閲覧専用。
  // サーバ側（admin Lambda）でも owner を強制するため、ここは UI 上の歯止め。
  const { isOwner } = useRole()
  // Current user's sub for self-deletion prevention in UI
  const currentUserId = (authUser?.profile?.sub as string) ?? undefined
  const [users, setUsers] = useState<User[]>([])
  const [loading, setLoading] = useState(true)
  const [inviteOpen, setInviteOpen] = useState(false)
  const [inviteEmail, setInviteEmail] = useState("")
  const [inviteRole, setInviteRole] = useState("member")
  const [inviting, setInviting] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<User | null>(null)
  // 失敗の可視化（2026-07-08）: 従来は console.error だけで「一覧が空のまま無言」に
  // なっていた（サーバ側の error.message をそのまま見せる・成功で消す）。
  const [errorMessage, setErrorMessage] = useState("")

  const fail = (prefix: string, e: unknown) => {
    console.error(prefix, e)
    setErrorMessage(
      e instanceof Error ? `${prefix}: ${e.message}` : prefix
    )
  }

  const loadUsers = useCallback(async () => {
    if (!token) return
    setLoading(true)
    try {
      setUsers(await listUsers(token))
      setErrorMessage("")
    } catch (e) {
      console.error("Failed to load users:", e)
      setErrorMessage(
        e instanceof Error
          ? `ユーザー一覧の取得に失敗しました: ${e.message}`
          : "ユーザー一覧の取得に失敗しました"
      )
    } finally {
      setLoading(false)
    }
  }, [token])

  useEffect(() => {
    loadUsers()
  }, [loadUsers])

  const handleRoleChange = async (userId: string, newRole: string) => {
    if (!token) return
    try {
      await updateUserRole(token, userId, newRole)
      await loadUsers()
    } catch (e) {
      fail("ロールの変更に失敗しました", e)
    }
  }

  const handleStatusToggle = async (userId: string, enabled: boolean) => {
    if (!token) return
    try {
      await updateUserStatus(token, userId, enabled)
      await loadUsers()
    } catch (e) {
      fail("有効/無効の切り替えに失敗しました", e)
    }
  }

  const handleDynamicAgentsToggle = async (userId: string, enabled: boolean) => {
    if (!token) return
    try {
      await setUserDynamicAgents(token, userId, enabled)
      await loadUsers()
    } catch (e) {
      fail("自由編成の切り替えに失敗しました", e)
    }
  }

  const handleInvite = async () => {
    if (!token || !inviteEmail) return
    setInviting(true)
    try {
      await inviteUser(token, inviteEmail, inviteRole)
      setInviteOpen(false)
      setInviteEmail("")
      setInviteRole("member")
      await loadUsers()
    } catch (e) {
      fail("招待に失敗しました", e)
    } finally {
      setInviting(false)
    }
  }

  const handleDelete = async () => {
    if (!token || !deleteTarget) return
    try {
      await deleteUser(token, deleteTarget.userId)
      setDeleteTarget(null)
      await loadUsers()
    } catch (e) {
      fail("ユーザーの削除に失敗しました", e)
    }
  }

  // 招待メール再送（owner のみ・仮パスワード未変更＝招待中のユーザーが対象）
  const [resendMessage, setResendMessage] = useState("")
  const handleResendInvite = async (email: string) => {
    if (!token) return
    try {
      await resendInvite(token, email)
      setResendMessage(`${email} へ招待メールを再送しました`)
    } catch (e) {
      setResendMessage(
        e instanceof Error ? `再送に失敗しました: ${e.message}` : "再送に失敗しました"
      )
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">ユーザー管理</h1>
        {/* 招待は owner のみ。admin は閲覧専用。 */}
        {isOwner && (
          <Button onClick={() => setInviteOpen(true)}>
            <UserPlus className="h-4 w-4 mr-2" />
            招待
          </Button>
        )}
      </div>
      {!isOwner && (
        <p className="text-sm text-muted-foreground">
          閲覧専用です。ロールの変更・招待・有効化/無効化・削除はオーナーのみ行えます。
        </p>
      )}
      {resendMessage && (
        <p className="text-sm text-muted-foreground bg-sky-50 border-l-4 border-sky-500 p-3">
          {resendMessage}
        </p>
      )}
      {errorMessage && (
        <p className="text-sm text-red-800 bg-red-50 border-l-4 border-red-500 p-3">
          {errorMessage}
        </p>
      )}

      <Card>
        <CardHeader>
          <CardTitle>ユーザー</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-2">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 px-3">ユーザー</th>
                    <th className="text-left py-2 px-3">ロール</th>
                    <th className="text-left py-2 px-3">ステータス</th>
                    <th
                      className="text-left py-2 px-3"
                      title="開放されたメンバーが、タスクで「自由編成で…」と明示した時だけ、AI がサブエージェント編成を組んで実行できる実験機能です。切り替えは次に登録するタスクから反映されます（約30秒）。"
                    >
                      自由編成
                    </th>
                    <th className="text-left py-2 px-3">操作</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user.userId} className="border-b">
                      <td className="py-2 px-3">
                        <div>{user.email}</div>
                        <div className="font-mono text-xs text-muted-foreground">
                          {user.userId.slice(0, 12)}...
                        </div>
                      </td>
                      <td className="py-2 px-3">
                        {isOwner ? (
                          <Select
                            value={user.role}
                            onValueChange={(val) =>
                              handleRoleChange(user.userId, val)
                            }
                          >
                            <SelectTrigger className="w-28">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {ROLES.map((r) => (
                                <SelectItem key={r} value={r}>
                                  {r}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <span className="text-sm">{user.role}</span>
                        )}
                      </td>
                      <td className="py-2 px-3">
                        <span
                          className={`px-2 py-0.5 rounded text-xs ${
                            user.enabled
                              ? "bg-green-100 text-green-800"
                              : "bg-red-100 text-red-800"
                          }`}
                        >
                          {user.enabled ? "有効" : "無効"}
                        </span>
                        {user.status === "FORCE_CHANGE_PASSWORD" && (
                          <span className="ml-1 px-2 py-0.5 rounded text-xs bg-amber-100 text-amber-800">
                            招待中
                          </span>
                        )}
                      </td>
                      <td className="py-2 px-3">
                        {/* 自由編成（run_subagents）の開放。切替は owner のみ（admin は閲覧専用）。 */}
                        <div className="flex items-center gap-2">
                          <span
                            className={`px-2 py-0.5 rounded text-xs ${
                              user.dynamicAgentsEnabled
                                ? "bg-violet-100 text-violet-800"
                                : "bg-gray-100 text-gray-500"
                            }`}
                          >
                            {user.dynamicAgentsEnabled ? "開放中" : "—"}
                          </span>
                          {isOwner && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() =>
                                handleDynamicAgentsToggle(
                                  user.userId,
                                  !user.dynamicAgentsEnabled
                                )
                              }
                            >
                              {user.dynamicAgentsEnabled ? "停止" : "開放"}
                            </Button>
                          )}
                        </div>
                      </td>
                      <td className="py-2 px-3">
                        {isOwner ? (
                          <div className="flex gap-1">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() =>
                                handleStatusToggle(user.userId, !user.enabled)
                              }
                            >
                              {user.enabled ? "無効化" : "有効化"}
                            </Button>
                            {/* 招待メール再送: 仮パスワード未変更（招待中）のユーザーのみ */}
                            {user.status === "FORCE_CHANGE_PASSWORD" && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleResendInvite(user.email)}
                                title="招待メールを再送"
                              >
                                <MailPlus className="h-4 w-4 mr-1" />
                                招待再送
                              </Button>
                            )}
                            {user.userId !== currentUserId && (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => setDeleteTarget(user)}
                              >
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            )}
                          </div>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Invite Dialog */}
      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>ユーザーを招待</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <Input
              placeholder="メールアドレス"
              type="email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
            />
            <Select value={inviteRole} onValueChange={setInviteRole}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="admin">admin</SelectItem>
                <SelectItem value="member">member</SelectItem>
                <SelectItem value="viewer">viewer</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInviteOpen(false)}>
              キャンセル
            </Button>
            <Button
              onClick={handleInvite}
              disabled={inviting || !inviteEmail}
            >
              {inviting ? "送信中..." : "招待"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>ユーザーを削除</AlertDialogTitle>
            <AlertDialogDescription>
              「{deleteTarget?.email}」を削除しますか？この操作は取り消せません。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeleteTarget(null)}>
              キャンセル
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-white hover:bg-destructive/90"
              onClick={handleDelete}
            >
              削除
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
